import { useTranslation } from "react-i18next";
import type { AnalyticsSummaryDto } from "@normax-performance/shared-types";
import { Donut, MetricChip, PerformanceHero } from "../../../shared-ui/primitives";
import { useLocale } from "../../../i18n/LocaleProvider";
import { formatAnalyticsHoursMinutes, formatAnalyticsPercent, formatAnalyticsTrend } from "../../../lib/format";

export interface PerformanceOverviewProps {
  summary: AnalyticsSummaryDto | undefined;
  isLoading: boolean;
}

/**
 * Analytics top summary (task: time-distribution simplification §6). Answers
 * two separate questions, never blended:
 *   1. "Učinak" — how well on the normed portion (Method C, the hero).
 *   2. "Where did the recorded time go" — the segmented time-distribution
 *      bar (Normirani rad / Neutralne aktivnosti / Rad bez norme, minutes + %
 *      against Ukupno evidentirano vreme).
 * "Ostvareni standardni minuti" is no longer a KPI here — the intermediate
 * only shows as the hero's help caption.
 */
export function PerformanceOverview({ summary, isLoading }: PerformanceOverviewProps) {
  const { t } = useTranslation("analytics");
  const { t: tCommon } = useTranslation("common");
  const { intlLocale } = useLocale();

  const overall = summary?.overall;
  const distribution = summary?.time.distribution;
  const ready = !isLoading && !!summary;

  return (
    <div className="nx-analytics__summary-block">
      <div className="nx-analytics__summary">
        <PerformanceHero
          label={t("overall.title")}
          isLoading={isLoading}
          percentDisplay={overall?.performancePercentLabel ? formatAnalyticsPercent(overall.performancePercentLabel, intlLocale) : null}
          unavailableLabel={t("notAvailable")}
          zone={overall?.zone?.zone ?? null}
          zoneBadgeLabel={overall?.zone ? tCommon(`performanceZone.${overall.zone.zone}`) : undefined}
          trendDisplay={formatAnalyticsTrend(overall?.trend.trendPercentagePoints ?? null)}
          trendDirection={overall?.trend.direction ?? null}
          trendSuffix={t("overall.trendVsPrevious")}
          caption={t("overall.performanceHelp")}
        />

        <div className="nx-summary-chips">
          <MetricChip
            label={t("summary.totalRecordedMinutes")}
            display={ready && distribution ? formatAnalyticsHoursMinutes(distribution.totalRecordedMinutes, intlLocale) : null}
          />
          <MetricChip
            label={t("summary.recordedWorkers")}
            display={ready ? String(summary.workers.recordedWorkerCount) : null}
          />
          <MetricChip
            label={t("summary.eligibleWorkers")}
            display={ready ? String(summary.workers.eligibleWorkerCount) : null}
          />
        </div>
      </div>

      {ready && distribution ? (
        <div className="nx-analytics__time-dist">
          <p className="nx-analytics__time-dist-title">{t("summary.timeDistributionTitle")}</p>
          <Donut
            segments={[
              { key: "normed", value: distribution.normedShare ?? 0, label: t("summary.normedWork"), minutes: distribution.normedMinutes, colorVar: "--color-row-normed" },
              { key: "neutral", value: distribution.neutralShare ?? 0, label: t("summary.neutralActivities"), minutes: distribution.neutralMinutes, colorVar: "--color-row-neutral" },
              { key: "unnormed", value: distribution.unnormedShare ?? 0, label: t("summary.unnormedWork"), minutes: distribution.unnormedMinutes, colorVar: "--color-row-unnormed" },
            ]}
            ariaLabel={t("summary.timeDistributionTitle")}
            intlLocale={intlLocale}
            hasData={distribution.normedShare !== null}
            minutesDisplay="hours"
            centerPrimary={formatAnalyticsHoursMinutes(distribution.totalRecordedMinutes, intlLocale)}
          />
        </div>
      ) : null}
    </div>
  );
}
